import logo from "../../../assets/appIcon.png"
import styles from "./styles.module.scss"
import { observer } from "mobx-react-lite"
import { useStores } from "../../../models/helpers/useStores"
import CopyOutline from "~icons/eva/copy-outline"
import CalendarOutline from "~icons/eva/calendar-outline"
import LinkOutline from "~icons/eva/link-2-outline"
import FlagOutline from "~icons/eva/flag-outline"
import PinOutline from "~icons/eva/pin-outline"
import { Link } from "react-router-dom"
import classNames from "classnames"
import { isSupported } from "firebase/messaging"
import { useShareLink } from "../../organisms/RoomList/useShareLink"
import { getLocationsText, getTravelDatesText } from "@cruisen/react-shared"
import { makeAutoObservable } from "mobx"
import { useEffect, useMemo } from "react"
import { ModalWrapper } from "../../templates/ModalWrapper"
import { Popover } from "react-tiny-popover"
import { QRCodeSVG } from "qrcode.react"
import QRCodeIcon from "../../assets/icons/QRCodeIcon"
import Config from "../../../config"
import { useMutation } from "@tanstack/react-query"
import { getStripeExternalSubscriptionManageUrl } from "../../../services/firebase-functions/billing"
import { formatCurrency } from "@cruisen/i18n"

type AvailableModals = "tripInvitation" | "qrCode"

class DashboardHeaderStore {
  activeModal: AvailableModals | null = null
  notificationsSupported: boolean = true
  notificationsEnabled: boolean = true

  constructor() {
    makeAutoObservable(this)
  }

  setNotificationsSupported(supported: boolean) {
    this.notificationsSupported = supported
  }

  setNotificationsEnabled(enabled: boolean) {
    this.notificationsEnabled = enabled
  }

  setActiveModal(modal: AvailableModals | null) {
    this.activeModal = modal
  }

  toggleActiveModal(modal: AvailableModals) {
    this.activeModal = this.activeModal === modal ? null : modal
  }
}

const getDaysUntilTrialEnds = (trialEnd: number) => {
  const now = new Date()
  const trialEndDate = new Date(trialEnd)
  const diffTime = Math.abs(trialEndDate.getTime() - now.getTime())
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const formatTrialEndDate = (trialEnd: number) => {
  const trialEndDate = new Date(trialEnd)
  return trialEndDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

export const DashboardHeader = observer(function DashboardHeader({
  headerBar,
}: {
  headerBar: string | null
}) {
  const rootStore = useStores()
  const store = useMemo(() => new DashboardHeaderStore(), [])
  const { copyLink, isLinkCopied, shareLink } = useShareLink()
  const isDemoTrip = rootStore.activeTrip?.tags.includes("demo")

  const departText = getLocationsText(
    rootStore.activeTrip?.originPortGroups?.map(({ name }) => name),
  )
  const destinationText = getLocationsText(
    rootStore.activeTrip?.destinationRegions?.map(({ name }) => name),
  )

  const { mutate: goToManageSubscription, isPending } = useMutation({
    mutationFn: () => getStripeExternalSubscriptionManageUrl(),
    onSuccess: (subscriptionManageUrl) => {
      if (subscriptionManageUrl) {
        window.open(subscriptionManageUrl, "_blank").focus()
      }
    },
  })

  useEffect(() => {
    isSupported()
      .then((supported) => {
        store.setNotificationsSupported(supported)

        if (supported && "Notification" in window) {
          const permission = Notification.permission
          store.setNotificationsEnabled(permission === "granted")
        }
      })
      .catch((err) => {
        console.error(err)
      })
  }, [])

  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission()
      store.setNotificationsEnabled(permission === "granted")
      if (permission === "granted") {
        location.reload()
      }
    } catch (err) {
      console.error("Error requesting notification permission:", err)
    }
  }

  const isBarVisible =
    headerBar === "pastDueBar" ||
    headerBar === "trialingBar" ||
    !store.notificationsSupported ||
    !store.notificationsEnabled

  if (!rootStore?.loggedInUser) return null

  return (
    <div>
      {headerBar === "pastDueBar" ? (
        <div className={classNames(styles.notificationsBanner, styles.notificationsBannerWarning)}>
          <p className={styles.notificationsBannerText}>
            We could not renew your subscription, please check your billing method.
          </p>
          <button
            className={styles.notificationsBannerButton}
            onClick={() => goToManageSubscription()}
            disabled={isPending}
          >
            {isPending ? "Loading..." : "Manage subscription"}
          </button>
        </div>
      ) : headerBar === "trialingBar" ? (
        <div className={classNames(styles.notificationsBanner, styles.notificationsBannerTrialing)}>
          <p className={styles.notificationsBannerTrialingText}>
            Your trial ends in {getDaysUntilTrialEnds(rootStore.stripeSubscription?.trialEnd)} days.
          </p>
          <button
            className={styles.notificationsBannerTrialingButton}
            onClick={() => rootStore.setProp("visibleModal", "startSubscription")}
          >
            Activiate your subscription
          </button>
          <p className={styles.notificationsBannerTrialingText}>
            by {formatTrialEndDate(rootStore.stripeSubscription?.trialEnd)} and get a{" "}
            <b>{formatCurrency(Config.stripeTrialPeriodEarlyPaymentMethodCredit / 100)} credit</b>.
          </p>
        </div>
      ) : !store.notificationsSupported || !store.notificationsEnabled ? (
        <div
          className={classNames(
            styles.notificationsBanner,
            !store.notificationsSupported
              ? styles.notificationsBannerError
              : styles.notificationsBannerWarning,
          )}
        >
          <p className={styles.notificationsBannerText}>
            {!store.notificationsSupported
              ? "Your browser doesn't support notifications. Some features may not work properly."
              : "Your browser has notifications disabled. Some features may not work properly."}
          </p>
          {!store.notificationsSupported ? null : (
            <button
              className={styles.notificationsBannerButton}
              onClick={requestNotificationPermission}
            >
              Enable Notifications
            </button>
          )}
        </div>
      ) : null}
      <div className={classNames(styles.header, isBarVisible && styles.headerWithWarning)}>
        <Link to="/dashboard">
          <img alt="cruisen logo" src={logo} className={styles.logoImage} />
        </Link>
        <div className={styles.headerRight}>
          <div className={styles.tripDetailsButton}>
            <div className={styles.tripDetails}>
              <CalendarOutline className={styles.tripDetailsIcon} />
              <p className={styles.tripDetailsText}>
                {getTravelDatesText(rootStore.activeTrip?.months)}
              </p>
            </div>
            <div className={styles.tripDetailsDivider} />
            <div className={styles.tripDetails}>
              <FlagOutline className={styles.tripDetailsIcon} />
              <p className={styles.tripDetailsText}>Depart {departText}</p>
            </div>
            <div className={styles.tripDetailsDivider} />
            <div className={styles.tripDetails}>
              <PinOutline className={styles.tripDetailsIcon} />
              <p className={styles.tripDetailsText} data-testid="trip-sail-to-text">
                Sail to {destinationText}
              </p>
            </div>
          </div>
          <Popover
            isOpen={store.activeModal === "tripInvitation"}
            positions={["bottom"]}
            padding={10}
            containerStyle={{ zIndex: "999" }}
            content={
              <div className={styles.shareLinkContainer}>
                <div className={classNames(styles.triangle, isDemoTrip && styles.triangleDemo)} />
                {isDemoTrip ? (
                  <div className={classNames(styles.shareLinkWrapper, styles.shareLinkWrapperDemo)}>
                    <p className={styles.shareLink}>You cannot invite users to a demo trip.</p>
                  </div>
                ) : (
                  <>
                    <div className={styles.shareLinkWrapper}>
                      <p className={styles.shareLink}>{shareLink}</p>
                    </div>
                    <button
                      className={classNames(
                        styles.greenButton,
                        Config.environment === "production" && styles.copyLinkButton,
                      )}
                      onClick={copyLink}
                    >
                      <LinkOutline className={styles.copyButtonIcon} />
                      <p className={styles.copyButtonText}>
                        {isLinkCopied ? "Copied" : "Copy link"}
                      </p>
                    </button>
                    {Config.environment !== "production" ? (
                      <>
                        <div className={styles.divider} />
                        <button
                          className={classNames(styles.greenButton, styles.qrCodeButton)}
                          onClick={() => {
                            if (!shareLink) {
                              alert(
                                "Link generation may not work properly if browser blockers are enabled; please disable them for the best experience.",
                              )
                              return
                            }
                            store.toggleActiveModal("qrCode")
                          }}
                        >
                          <QRCodeIcon />
                          <p className={styles.copyButtonText}>QR code</p>
                        </button>
                      </>
                    ) : null}
                  </>
                )}
              </div>
            }
            onClickOutside={() => store.setActiveModal(null)}
          >
            <button
              data-test-id="trip-invitation-button"
              className={styles.tripInvitationButton}
              type="button"
              onClick={() => store.toggleActiveModal("tripInvitation")}
            >
              <CopyOutline className={styles.tripInvitationIcon} />
              <p className={styles.tripInvitationText}>Trip invitation</p>
            </button>
          </Popover>
        </div>
        {store.activeModal === "qrCode" ? (
          <ModalWrapper onClose={() => store.toggleActiveModal(null)}>
            <div className={styles.qrCodeContainer}>
              <h2 className={styles.qrCodeTitle}>Trip invitation</h2>
              <p className={styles.qrCodeDescription}>
                Scan or share this QR code to instantly join!
              </p>
              <QRCodeSVG value={shareLink} size={160} bgColor="transparent" fgColor="#fff" />
              <button
                onClick={() => store.toggleActiveModal(null)}
                className={styles.qrCodeDoneButton}
              >
                Done
              </button>
            </div>
          </ModalWrapper>
        ) : null}
      </div>
    </div>
  )
})
